import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Frelan: "desktop" for the card finish overlay — a mouse on a desktop-width screen. Here the
 * overlay follows Theme settings → Product cards → Finishes on cards → Desktop; everywhere else
 * (phones, touch tablets/laptops) it follows the Mobile setting.
 */
const finishDesktopQuery = matchMedia('(min-width: 750px) and (hover: hover)');

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#unbindFinishOverlay();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Frelan: choosing a finish in the overlay closes it, revealing the newly selected variant.
    // Not the swatch pill: there picking a finish keeps it open so you can carry on browsing.
    if (this.finishesOpen && !this.#finishPill) this.closeFinishes();

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    // Frelan: clicks anywhere inside the finish overlay (panel padding, heading) never navigate.
    if (event.target.closest('.card-finishes')) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);

  /* ------------------------------------------------------------------
     Frelan: finish overlay (snippets/card-finish-overlay.liquid).
     A panel covering the card image that lists the finishes as rows (swatch,
     name, variant SKU). Opened from the SKU line (blocks/sku.liquid,
     on:click="product-card/toggleFinishes"), closed by its × button, a
     pointer-down outside the card, or Escape. State lives on this element as
     `data-finishes-open` (CSS drives the fade). Only one card is open at a time.
     ------------------------------------------------------------------ */

  /** @returns {HTMLElement | null} */
  get finishOverlay() {
    const overlay = this.querySelector('.card-finishes');
    return overlay instanceof HTMLElement ? overlay : null;
  }

  /** @returns {HTMLElement | null} */
  get finishTrigger() {
    const trigger = this.querySelector('.sku-finishes-trigger');
    return trigger instanceof HTMLElement ? trigger : null;
  }

  get finishesOpen() {
    return this.hasAttribute('data-finishes-open');
  }

  /** Whether the open overlay is the swatch pill (set on open from the theme settings). */
  #finishPill = false;

  /** Pointer type of the last press inside the overlay, so a click knows if it was a tap. */
  #finishPointerType = '';

  /**
   * Toggles the finish overlay. Bound to the SKU trigger button.
   * @param {Event} [event]
   */
  toggleFinishes = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.finishesOpen) {
      this.closeFinishes();
    } else {
      this.openFinishes();
    }
  };

  openFinishes() {
    const overlay = this.finishOverlay;
    if (!overlay || this.finishesOpen) return;

    for (const other of document.querySelectorAll('product-card[data-finishes-open]')) {
      if (other !== this && other instanceof ProductCard) other.closeFinishes();
    }

    const style = finishDesktopQuery.matches ? overlay.dataset.desktopStyle : overlay.dataset.mobileStyle;
    this.#finishPill = style === 'pill';
    overlay.classList.toggle('card-finishes--pill', this.#finishPill);

    this.setAttribute('data-finishes-open', '');
    overlay.setAttribute('aria-hidden', 'false');
    this.finishTrigger?.setAttribute('aria-expanded', 'true');

    document.addEventListener('pointerdown', this.#handleFinishOutsidePointer, true);
    document.addEventListener('keydown', this.#handleFinishKeydown);
    overlay.addEventListener('pointerover', this.#handlePillHover);
    overlay.addEventListener('pointerdown', this.#handlePillPointerDown);
    overlay.addEventListener('click', this.#handlePillClick);
  }

  /**
   * Closes the finish overlay. Bound to the panel's × button.
   * @param {Event} [event]
   */
  closeFinishes = (event) => {
    event?.stopPropagation();
    if (!this.finishesOpen) return;

    this.removeAttribute('data-finishes-open');
    this.finishOverlay?.setAttribute('aria-hidden', 'true');
    this.finishTrigger?.setAttribute('aria-expanded', 'false');
    this.#unbindFinishOverlay();

    // Return focus to the trigger when closed from the keyboard or the × button.
    if (event && this.finishTrigger && event.type !== 'pointerdown') {
      this.finishTrigger.focus({ preventScroll: true });
    }
  };

  #unbindFinishOverlay() {
    document.removeEventListener('pointerdown', this.#handleFinishOutsidePointer, true);
    document.removeEventListener('keydown', this.#handleFinishKeydown);
    this.finishOverlay?.removeEventListener('pointerover', this.#handlePillHover);
    this.finishOverlay?.removeEventListener('pointerdown', this.#handlePillPointerDown);
    this.finishOverlay?.removeEventListener('click', this.#handlePillClick);
    for (const row of this.querySelectorAll('.finish-row[data-pill-active]')) row.removeAttribute('data-pill-active');
  }

  /**
   * Swatch pill, mouse: hovering a swatch selects that finish, so the image, SKU and links stay
   * on it after the pointer moves away. (On the panel, hover only previews and a click selects.)
   * @param {PointerEvent} event
   */
  #handlePillHover = (event) => {
    if (event.pointerType !== 'mouse' || !this.#finishPill) return;
    const input = this.#pillInputFor(event.target);
    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** @param {PointerEvent} event */
  #handlePillPointerDown = (event) => {
    this.#finishPointerType = event.pointerType;
  };

  /**
   * Swatch pill: a click opens the product page on that finish. A tap on a finish that isn't
   * selected yet selects it instead (the label's normal radio behaviour) and keeps its name
   * showing, so on touch the first tap shows the finish and the second opens it.
   * @param {MouseEvent} event
   */
  #handlePillClick = (event) => {
    // The label passes each click on to its radio as a second click; only act on the first.
    if (!this.#finishPill || event.target instanceof HTMLInputElement) return;
    const input = this.#pillInputFor(event.target);
    const variantId = input?.dataset.variantId;
    if (!input || !variantId || !this.refs.productCardLink.href) return;

    const isTap = this.#finishPointerType === 'touch' || this.#finishPointerType === 'pen';
    this.#finishPointerType = '';
    if (isTap && !input.checked) {
      for (const row of this.querySelectorAll('.finish-row[data-pill-active]')) row.removeAttribute('data-pill-active');
      input.closest('.finish-row')?.setAttribute('data-pill-active', '');
      return;
    }

    event.preventDefault();
    const url = new URL(this.refs.productCardLink.href);
    url.searchParams.set('variant', variantId);
    this.#navigateToURL(event, url);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLInputElement | null} The radio of the finish row the target is in.
   */
  #pillInputFor(target) {
    if (!(target instanceof Element)) return null;
    const input = target.closest('.finish-row__label')?.querySelector('input[type="radio"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {PointerEvent} event */
  #handleFinishOutsidePointer = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.closeFinishes(event);
  };

  /** @param {KeyboardEvent} event */
  #handleFinishKeydown = (event) => {
    if (event.key === 'Escape') this.closeFinishes(event);
  };
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Frelan: inside the card finish overlay the swatches are rendered as rows
   * (snippets/variant-swatches.liquid, layout 'finish-rows') while the section
   * rendering endpoint returns Horizon's standard swatch list. Morphing the two
   * would replace the rows, so here we only sync what changes per variant —
   * checked state and availability — by option value id, and let the base class
   * handle everything else (price, image, URL, SKU) from the same response.
   * @param {Document} newHtml
   */
  updateVariantPicker(newHtml) {
    if (!this.closest('.card-finishes')) {
      return super.updateVariantPicker(newHtml);
    }

    const source = newHtml.querySelector(this.tagName.toLowerCase());
    if (!(source instanceof HTMLElement)) return undefined;

    /** @type {{ id: string, url: string } | undefined} */
    let newProduct;
    const newProductId = source.dataset.productId;
    const newProductUrl = source.dataset.productUrl;
    if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
      newProduct = { id: newProductId, url: newProductUrl };
    }
    if (newProductId) this.dataset.productId = newProductId;
    if (newProductUrl) this.dataset.productUrl = newProductUrl;

    for (const input of this.querySelectorAll('input[data-option-value-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const fresh = source.querySelector(`input[data-option-value-id="${input.dataset.optionValueId}"]`);
      if (!(fresh instanceof HTMLInputElement)) continue;

      input.checked = fresh.checked;
      input.dataset.optionAvailable = fresh.dataset.optionAvailable ?? input.dataset.optionAvailable;
      if (fresh.dataset.variantId) input.dataset.variantId = fresh.dataset.variantId;
      if (fresh.dataset.optionMediaId) input.dataset.optionMediaId = fresh.dataset.optionMediaId;
    }

    return newProduct;
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
